<template>
  <RevTable
    v-model:checked-ids="selectedIds"
    :collection="orders"
    :columns
    :getCollectionItemId="(order) => order.id.toString()"
    striped-rows
    transparent-header
  >
    <template #body-order="{ item }">
      <OrderCell :order="item" />
    </template>
    <template #body-shipping="{ item }">
      <ShippingCell :order="item" :refreshOrders />
    </template>
    <template #body-orderlines="{ item }">
      <OrderLinesCell :order="item" />
    </template>
    <template #body-payments="{ item }">
      <PaymentsCell :order="item" />
    </template>
    <template #body-status="{ item }">
      <StatusCell :order="item" />
    </template>
    <template #body-action="{ item }">
      <ActionCell :order="item" :refreshOrders />
    </template>
  </RevTable>
</template>

<script setup lang="ts">
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { RevTable } from '@ds/components/Table'

import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'

import type { Orders } from '../../Tab.types'

import translations from './Table.translations'
import ActionCell from './components/ActionCell/ActionCell.vue'
import OrderCell from './components/OrderCell/OrderCell.vue'
import OrderLinesCell from './components/OrderLinesCell/OrderLinesCell.vue'
import PaymentsCell from './components/PaymentsCell/PaymentsCell.vue'
import ShippingCell from './components/ShippingCell/ShippingCell.vue'
import StatusCell from './components/StatusCell/StatusCell.vue'

const i18n = useI18n()

defineProps<{
  orders: Orders
  refreshOrders: () => void
}>()

const selectedIds = defineModel<string[]>('selectedIds', {
  required: true,
})

const { isFeatureEnabled } = useBusinessFeaturesStore()

const isDynamicPricingUpEnabled = isFeatureEnabled(
  'isDynamicPricingUpFirstIterationEnabled',
)

const columns = [
  {
    key: 'order',
    label: i18n(translations.order),
  },
  {
    key: 'shipping',
    label: i18n(translations.shipping),
  },
  {
    key: 'orderlines',
    label: i18n(translations.orderLines),
  },
  {
    key: 'payments',
    label: i18n(translations.payments),
    ...insertIf(isDynamicPricingUpEnabled, {
      description: i18n(translations.paymentsDetailsTooltip),
    }),
  },
  {
    key: 'status',
    label: i18n(translations.status),
  },
  {
    key: 'action',
    label: '',
    align: 'end' as const,
  },
]
</script>
